"use client";

import type { ComponentConfig } from "@puckeditor/core";
import { useQuery } from "convex/react";
import Image from "next/image";
import Link from "next/link";
import { api } from "@/convex/_generated/api";
import { staticBlogArticles } from "@/data/blog-articles";
import { resolveMediaUrl } from "@/lib/resolve-media-url";

type ManagedBlog = {
  _id?: string;
  slug: string;
  title: string;
  excerpt?: string;
  coverImage?: string;
  author?: string;
  publishedAt?: number;
  _creationTime?: number;
};

export interface BlogIndexBlockProps {
  heading?: string;
  intro?: string;
  readLabel?: string;
  fallbackAuthor?: string;
  fallbackMeta?: string;
  emptyHeading?: string;
  emptyBody?: string;
  showAuthor?: boolean;
  initialData?: { initialBlogs?: ManagedBlog[] };
}

function BlogIndexBlock({
  heading,
  intro,
  readLabel,
  fallbackAuthor,
  fallbackMeta,
  emptyHeading,
  emptyBody,
  showAuthor = true,
  initialData,
}: BlogIndexBlockProps) {
  const convexConfigured = Boolean(process.env.NEXT_PUBLIC_CONVEX_URL);
  const liveBlogs = useQuery(api.blogs.listBlogs, convexConfigured ? {} : "skip");
  const managedBlogs = (liveBlogs || initialData?.initialBlogs || []) as ManagedBlog[];
  const managedSlugs = new Set(managedBlogs.map((blog) => blog.slug));
  const articles = [
    ...managedBlogs.map((blog) => ({
      slug: blog.slug,
      title: blog.title,
      excerpt: blog.excerpt || "Manufacturing and formulation guidance from Shaaira Biocare.",
      coverImage: resolveMediaUrl(blog.coverImage, "/og-lab-v2.jpg"),
      author: blog.author || fallbackAuthor,
      meta: fallbackMeta,
    })),
    ...staticBlogArticles
      .filter((article) => !managedSlugs.has(article.slug))
      .map((article) => ({
        slug: article.slug,
        title: article.title,
        excerpt: article.excerpt,
        coverImage: article.coverImage,
        author: article.author,
        meta: article.readingTime,
      })),
  ];

  return (
    <div className="blog-index-page">
      <section className="blog-index-hero">
        <div className="container">
          <h1>{heading}</h1>
          {intro && <p>{intro}</p>}
        </div>
      </section>

      <section className="sec">
        <div className="container">
          {articles.length > 0 ? (
            <div className="blog-card-grid">
              {articles.map((article) => (
                <Link className="blog-card" href={`/blogs/${article.slug}`} key={article.slug}>
                  <span className="blog-card-media">
                    <Image src={article.coverImage} alt="" fill sizes="(max-width: 760px) 100vw, 33vw" />
                  </span>
                  <span className="blog-card-body">
                    {(showAuthor || article.meta) && (
                      <span className="blog-card-meta">
                        {showAuthor && article.author}
                        {showAuthor && article.meta && <span aria-hidden="true"> · </span>}
                        {article.meta}
                      </span>
                    )}
                    <strong>{article.title}</strong>
                    <span className="blog-card-excerpt">{article.excerpt}</span>
                    <span className="blog-card-link">
                      {readLabel}
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="blog-index-empty">
              <h2>{emptyHeading}</h2>
              <p>{emptyBody}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export const BlogIndexBlockConfig: ComponentConfig<BlogIndexBlockProps> = {
  label: "Blog Index",
  fields: {
    heading: { type: "text", label: "Page Heading" },
    intro: { type: "textarea", label: "Introduction" },
    readLabel: { type: "text", label: "Article Link Label" },
    fallbackAuthor: { type: "text", label: "Default Author" },
    fallbackMeta: { type: "text", label: "Default Article Meta" },
    showAuthor: {
      type: "radio",
      label: "Show Authors",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    emptyHeading: { type: "text", label: "Empty-State Heading" },
    emptyBody: { type: "textarea", label: "Empty-State Message" },
  },
  defaultProps: {
    heading: "Formulation & Manufacturing Notes",
    intro: "Practical guidance for beauty founders planning product briefs, samples, packaging, quality records, and private-label production.",
    readLabel: "Read article",
    fallbackAuthor: "Shaaira Biocare Team",
    fallbackMeta: "Article",
    showAuthor: true,
    emptyHeading: "No notes published yet",
    emptyBody: "New formulation and manufacturing guidance will appear here when it is published.",
  },
  render: (props) => <BlogIndexBlock {...props} />,
};
